/**
 * BUSCA DE EMPRESA POR ID **OU** RAZÃO SOCIAL — pedido do usuário (17/09):
 * "permita pesquisar a empresa por ID ou por razão social também, substituindo o
 * dropselection existente hoje".
 *
 * POR QUE NÃO REUSAR `casaBuscaPorModo` (utils/codigoEmpresa.ts)
 * -------------------------------------------------------------
 * Aquela função existe para a aba Resumo, onde há um SELETOR DE MODO: o usuário
 * escolhe "razão social" ou "id" e a busca casa só naquele campo. O pedido aqui é
 * o oposto — um campo só que aceita os dois, sem o usuário ter que declarar o que
 * está digitando. Reusar a de lá exigiria chamá-la duas vezes e unir, o que na
 * prática é outra função; escrever esta explicitamente deixa a regra visível e
 * testável, e não mexe no comportamento da aba Resumo, que ninguém pediu para
 * mudar.
 *
 * ⚠️ HOMÔNIMOS — A ARMADILHA DESTE CAMPO
 * --------------------------------------
 * `codi_emp` REPETE entre cadastros: o código `715` aparece em 22 cadastros e o
 * `202` em grupos diferentes. Então "buscar por id" pode casar VÁRIAS empresas
 * distintas, e colapsá-las numa linha só (ou pegar "a primeira") entregaria a
 * empresa errada com aparência de acerto.
 *
 * Por isso:
 * - a busca devolve uma LISTA, nunca um item;
 * - `rotuloDesambiguado` acrescenta o grupo/CNPJ quando duas opções compartilham o
 *   mesmo par (nome, código) — o usuário vê as duas e escolhe;
 * - `temHomonimos` diz quando a lista tem colisão, para a UI poder avisar.
 *
 * ORDENAÇÃO: casamento por CÓDIGO EXATO primeiro. Quem digita "715" quase sempre
 * sabe o código e quer aquele; deixá-lo atrás de 30 razões sociais que contêm
 * "715" em algum lugar tornaria o campo inútil para o uso mais comum.
 */

import type { IEmpresaResumo } from '../api';

/** Uma opção pesquisável — o mínimo que a busca precisa, desacoplado da API. */
export interface OpcaoEmpresa {
  codiEmp: string;
  /** Nome exibível (razão social ou fantasia, o que `nomeEmpresa()` resolveu). */
  nome: string;
  /** Grupo de empresas — desambigua homônimos. */
  grupo?: string | null;
  /** CNPJ — desambigua quando nem o grupo resolve. */
  cnpj?: string | null;
}

/** Normaliza para comparação: sem acento, sem caixa, sem espaço nas pontas. */
export const normalizar = (texto: string): string =>
  (texto ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    // eslint-disable-next-line no-misleading-character-class
    .replace(/[̀-ͯ]/g, '');

/** Só dígitos — para o CNPJ e para reconhecer termo puramente numérico. */
const soDigitos = (texto: string): string => (texto ?? '').replace(/\D/g, '');

/**
 * Converte a lista de empresas da página em opções pesquisáveis.
 *
 * `nomeDaEmpresa` é injetado (não importado) porque a resolução do nome já existe
 * em `derivarProcessos.nomeEmpresa` e tem fallback próprio para cadastro sem nome —
 * duplicar essa regra aqui criaria a segunda fonte que o repositório já sofre em
 * outros lugares (a dedup mora em dois sítios e isso é um risco conhecido).
 *
 * SÓ ENTRA QUEM É EMPRESA DO CADASTRO (28/09). Linha sem `empresa` é rastro de bot
 * cuja chave não casa com nenhum cadastro — não tem nome para mostrar nem regime
 * para filtrar, e num campo de BUSCA DE EMPRESA ela só ocupa lugar.
 *
 * O dono viu o campo abrir com uma parede de "sem razão social cadastrada" e
 * disse que antes funcionava. Funcionava: até 21/09 a tela abria em 7 dias, que é
 * servido pelo Mongo; de lá para cá abre em janela de 8 dias ou mais, servida
 * pelo lakehouse, onde existem bots que gravam outra coisa no `codi_emp`. Medido
 * em produção em 28/09, janela "Mês atual": 78 linhas do `WS_NF_GUIA_PORTAL_SP`,
 * que grava INSCRIÇÃO MUNICIPAL — `tb_empresa` não tem essa coluna, então não há
 * nome a puxar. Em "7 dias" são 0.
 *
 * E por a ordenação ser alfabética, essas linhas (cujo "nome" era o próprio
 * código) ocupavam o TOPO: 49 das 50 primeiras.
 *
 * O filtro é pela existência do CADASTRO, não do nome: empresa cadastrada sem
 * razão social continua na lista, com o aviso de que falta o nome — essa é uma
 * empresa de verdade com o cadastro incompleto, e escondê-la esconderia o defeito.
 *
 * Só o campo de busca muda. As linhas seguem no `/resumo`, nas contagens e na aba
 * Resumo — isto não é uma regra de identidade, é o que cabe num seletor de empresa.
 */
export const opcoesDeEmpresas = (
  empresas: IEmpresaResumo[],
  nomeDaEmpresa: (e: IEmpresaResumo) => string,
): OpcaoEmpresa[] =>
  empresas
    .filter(e => Boolean(e.empresa))
    .map(e => ({
      codiEmp: e.codi_emp,
      nome: nomeDaEmpresa(e),
      grupo: e.empresa?.grupo_de_empresas ?? null,
      cnpj: e.empresa?.cnpj ?? null,
    }));

/**
 * A opção TEM nome de verdade, ou o "nome" é só o código repetido?
 *
 * `nomeEmpresa` (derivarProcessos) cai no `codi_emp` quando o cadastro não tem
 * razão social — e isso acontece de verdade: o `codi_emp` do Mongo casa com
 * `tb_empresa.external_id`, e a empresa que só existe como rastro de execução não
 * tem linha em `tb_empresa` para enriquecer. O resultado na lista era o código
 * impresso NAS DUAS colunas ("103 … 103"), que o dono apontou em 25/09.
 *
 * Aqui não se inventa nome: quem não tem fica só com o código, numa coluna só. A
 * regra é a mesma de `rotuloDesambiguado`, extraída para os dois usarem uma
 * definição só — a duplicata entre rótulo e render é como a divergência começa.
 */
export const temNomeProprio = (opcao: OpcaoEmpresa): boolean =>
  Boolean(opcao.nome) && normalizar(opcao.nome) !== normalizar(opcao.codiEmp);

/** A opção casa o termo por código (contains) OU por nome (contains)? */
export const casaTermo = (opcao: OpcaoEmpresa, termoNormalizado: string): boolean => {
  if (!termoNormalizado) return true;
  const codigo = normalizar(opcao.codiEmp);
  const nome = normalizar(opcao.nome);
  if (codigo.includes(termoNormalizado) || nome.includes(termoNormalizado)) return true;
  // CNPJ digitado com ou sem máscara — quem cola o CNPJ do cadastro espera achar.
  const digitos = soDigitos(termoNormalizado);
  return digitos.length >= 4 && soDigitos(opcao.cnpj ?? '').includes(digitos);
};

/**
 * Filtra e ORDENA as opções para o termo digitado.
 *
 * Ordem: código exato > código começa com > nome começa com > resto, e dentro de
 * cada faixa por nome (pt-BR). O empate por nome mantém a lista estável entre
 * digitações — lista que reordena sozinha a cada tecla é impossível de clicar.
 *
 * `limite` existe porque a lista bruta tem ~1,6 mil empresas e renderizar todas a
 * cada tecla trava o campo. Termo vazio devolve o começo da lista (o usuário está
 * só abrindo o seletor), e a UI avisa quando há mais do que o limite.
 */
export const buscarEmpresas = (
  opcoes: OpcaoEmpresa[],
  termo: string,
  limite = 50,
): OpcaoEmpresa[] => {
  const t = normalizar(termo);
  const casadas = t ? opcoes.filter(o => casaTermo(o, t)) : opcoes.slice();

  const peso = (o: OpcaoEmpresa): number => {
    if (!t) return 3;
    const codigo = normalizar(o.codiEmp);
    const nome = normalizar(o.nome);
    if (codigo === t) return 0;
    if (codigo.startsWith(t)) return 1;
    if (nome.startsWith(t)) return 2;
    return 3;
  };

  return casadas
    .sort((a, b) => {
      const d = peso(a) - peso(b);
      if (d !== 0) return d;
      const porNome = a.nome.localeCompare(b.nome, 'pt-BR');
      // Desempate final pelo código: duas empresas com o MESMO nome (homônimas)
      // precisam de ordem determinística, senão trocam de lugar entre renders.
      return porNome !== 0 ? porNome : a.codiEmp.localeCompare(b.codiEmp, 'pt-BR');
    })
    .slice(0, limite);
};

/**
 * Há duas opções na lista com o MESMO par (nome, código)?
 *
 * É o sinal de que o rótulo simples não distingue e a UI precisa mostrar o
 * desempate. Não confundir com "mesmo código, nomes diferentes" — esse caso o
 * rótulo já separa sozinho, porque o nome aparece.
 */
export const temHomonimos = (opcoes: OpcaoEmpresa[]): boolean => {
  const vistos = new Set<string>();
  return opcoes.some(o => {
    const chave = `${normalizar(o.nome)}|${normalizar(o.codiEmp)}`;
    if (vistos.has(chave)) return true;
    vistos.add(chave);
    return false;
  });
};

/**
 * O rótulo de UMA opção, desambiguado SÓ quando precisa.
 *
 * Sem colisão o rótulo é o template único da casa ("Nome_1117" — `rotuloComCodigo`
 * em utils/codigoEmpresa.ts). Com colisão, acrescenta o grupo e, se o grupo também
 * empatar, os 8 primeiros dígitos do CNPJ (a raiz, que é o que distingue matriz de
 * filial e é legível de bater com o cadastro).
 *
 * NUNCA colapsa duas empresas numa linha: o pedido é explícito em mostrá-las
 * distintamente, e escolher "a primeira" por baixo dos panos entregaria a empresa
 * errada com cara de acerto.
 */
export const rotuloDesambiguado = (opcao: OpcaoEmpresa, todas: OpcaoEmpresa[]): string => {
  const base = temNomeProprio(opcao)
    ? `${opcao.nome}_${opcao.codiEmp}`
    : opcao.codiEmp || opcao.nome;

  const mesmoParte = todas.filter(
    o =>
      normalizar(o.nome) === normalizar(opcao.nome) &&
      normalizar(o.codiEmp) === normalizar(opcao.codiEmp),
  );
  if (mesmoParte.length <= 1) return base;

  const grupo = (opcao.grupo ?? '').trim();
  const mesmoGrupo = mesmoParte.filter(o => normalizar(o.grupo ?? '') === normalizar(grupo));
  if (grupo && mesmoGrupo.length <= 1) return `${base} · ${grupo}`;

  const raiz = soDigitos(opcao.cnpj ?? '').slice(0, 8);
  if (raiz) return grupo ? `${base} · ${grupo} · ${raiz}` : `${base} · ${raiz}`;
  return grupo ? `${base} · ${grupo}` : base;
};

/**
 * Quantas opções casam o termo — só a CONTAGEM.
 *
 * Existe para a frase "mostrando N de M": `buscarEmpresas` com limite infinito
 * responderia o mesmo número, mas pagaria um `sort` completo da lista de ~1,6 mil
 * empresas a cada tecla, para depois descartar tudo menos o `length`. Aqui é um
 * `filter` sem ordenação.
 */
export const contarEmpresas = (opcoes: OpcaoEmpresa[], termo: string): number => {
  const t = normalizar(termo);
  return t ? opcoes.filter(o => casaTermo(o, t)).length : opcoes.length;
};
